import { useState, useCallback, useEffect } from 'react';
import { getUserProfile, updateUserPinnedItems, clearUserProfileCache } from '../../../services/userService';
import { getPathLabel } from '../navigationUtils';

export const useNavigationHistory = (user) => {
  const [navigationHistory, setNavigationHistory] = useState([]);
  const [pinnedItems, setPinnedItems] = useState([]);
  const [hasPermissionError, setHasPermissionError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Function to load pinned items from user profile
  const loadPinnedItems = useCallback(async (userUid) => {
    if (!userUid) return;
    
    try {
      setIsLoading(true);
      console.log('Loading pinned items for user:', userUid);
      
      // Clear any cached user profile data to ensure we get fresh data
      clearUserProfileCache(userUid);
      
      // Force a fresh fetch of user profile data
      const userProfile = await getUserProfile(userUid, true);
      console.log('User profile loaded, checking pinned items...');
      
      if (userProfile.pinnedNavigationItems && Array.isArray(userProfile.pinnedNavigationItems)) {
        setPinnedItems(userProfile.pinnedNavigationItems);
        console.log('Pinned items loaded:', userProfile.pinnedNavigationItems.length, 'items');
        console.log('Pinned items data:', userProfile.pinnedNavigationItems);
      } else {
        console.log('No pinned items found in user profile');
        console.log('User profile keys:', Object.keys(userProfile || {}));
        console.log('User profile pinnedNavigationItems:', userProfile.pinnedNavigationItems);
        setPinnedItems([]);
      }
      
      // Reset permission error if load succeeds
      setHasPermissionError(false);
    } catch (error) {
      console.error('Error loading pinned items:', error);
      // Don't throw the error, just log it to prevent blocking the UI
      if (error.code === 'permission-denied') {
        console.warn('Pinned items: Insufficient permissions - user may not be fully authenticated yet');
        setHasPermissionError(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update navigation history locally (but don't save to Firestore since only pinned items should persist)
  const updateNavigationHistory = useCallback((path) => {
    // Skip only auth-related paths that shouldn't be in history
    if (path === '/login' || path === '/logout') {
      return;
    }

    // Only track navigation history locally for current session
    // Don't save to Firestore since only pinned items should persist
    setNavigationHistory(prev => {
      // Get existing item if it exists
      const existingItem = prev.find(item => item.path === path);

      // Create new item with label and timestamp
      const newItem = {
        path,
        label: getPathLabel(path),
        timestamp: Date.now(),
        isPinned: existingItem?.isPinned || false
      };

      // Filter out old version of this path
      const filtered = prev.filter(item => item.path !== path);

      // Add new item at the start
      const newHistory = [newItem, ...filtered];

      // Keep only the last 5 items
      const finalHistory = newHistory.slice(0, 5);

      // Don't save to Firestore - only pinned items should persist
      console.log('Navigation history updated locally (not saved to Firestore)');

      return finalHistory;
    });
  }, []);

  // Function to save pinned items to Firestore
  const savePinnedItemsToFirestore = useCallback(async (pinned) => {
    if (!user?.uid) return;
    
    try {
      console.log('Saving pinned items for user:', user.uid);
      await updateUserPinnedItems(user.uid, pinned);
      console.log('Pinned items saved successfully');
    } catch (error) {
      console.error('Error saving pinned items:', error);
      if (error.code === 'permission-denied') {
        console.warn('Pinned items: Cannot save - insufficient permissions');
        setHasPermissionError(true);
      }
      // Don't throw the error to avoid blocking UI
    }
  }, [user?.uid]);

  // Function to toggle pin status of an item
  const handleTogglePin = useCallback((item, event) => {
    event.stopPropagation(); // Prevent navigation when clicking pin
    
    const isPinned = pinnedItems.some(pinned => pinned.path === item.path);
    
    if (isPinned) {
      // Unpin the item
      const newPinnedItems = pinnedItems.filter(pinned => pinned.path !== item.path);
      setPinnedItems(newPinnedItems);
      
      // Save to Firestore only if no permission errors
      if (!hasPermissionError) {
        savePinnedItemsToFirestore(newPinnedItems);
      } else {
        console.log('Skipping pinned items save due to permission error');
      }
    } else {
      // Pin the item
      const pinnedItem = { ...item, isPinned: true };
      const newPinnedItems = [...pinnedItems, pinnedItem];
      setPinnedItems(newPinnedItems);
      
      // Save to Firestore only if no permission errors
      if (!hasPermissionError) {
        savePinnedItemsToFirestore(newPinnedItems);
      } else {
        console.log('Skipping pinned items save due to permission error');
      }
    }
  }, [pinnedItems, savePinnedItemsToFirestore, hasPermissionError]);

  // Load pinned items when user changes
  useEffect(() => {
    if (user?.uid) {
      // Load pinned items immediately
      loadPinnedItems(user.uid);
    } else {
      // Clear navigation history and pinned items when user is not authenticated
      setNavigationHistory([]);
      setPinnedItems([]);
      setHasPermissionError(false);
    }
  }, [user?.uid, loadPinnedItems]);

  // Retry loading if there was a permission error
  useEffect(() => {
    if (hasPermissionError && user?.uid) {
      console.log('Retrying pinned items load after permission error...');
      const retryTimeout = setTimeout(() => {
        loadPinnedItems(user.uid);
      }, 2000); // Retry after 2 seconds

      return () => clearTimeout(retryTimeout);
    }
  }, [hasPermissionError, user?.uid, loadPinnedItems]);

  return {
    navigationHistory, // Recent navigation history for current session
    pinnedItems, // Pinned items that persist across sessions
    updateNavigationHistory,
    handleTogglePin,
    loadPinnedItems: () => loadPinnedItems(user?.uid),
    hasPermissionError,
    isLoading
  };
}; 